import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Loader2, Palette } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };

/** Swatch — pastille couleur + nom d'un matériau résolu. */
export const MaterialSwatch = ({ mat, label }) => (
  <span className="inline-flex items-center gap-1 text-[9.5px] px-1.5 py-0.5 rounded border border-white/10 bg-white/[0.04] text-gray-300 max-w-full" title={`${label} : ${mat.name}`}>
    <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: mat.thumb_color || '#6b7280' }} />
    <span className="truncate">{label} · {mat.name}</span>
  </span>
);

/** QuickAddInline — crée un matériau du projet dans un rôle extérieur sans quitter le plan de masse. */
const QuickAddInline = ({ projectId, slot, lib, onCreated, onCancel }) => {
  const [name, setName] = useState('');
  const [subrole, setSubrole] = useState(slot.subroles[0]);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/materialtheque/projects/${projectId}/quick-add`, { name: name.trim(), subrole }, opts);
      toast.success(`« ${name.trim()} » ajouté à la Matériauthèque`);
      onCreated(r.data.material_id);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Création impossible'); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-1 p-1.5 rounded border border-amber-300/30 bg-amber-400/[0.06] space-y-1" data-testid={`site-slot-quickadd-${slot.key}`}>
      <input autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') onCancel(); }}
             placeholder="Nom (ex : Enduit taloché blanc cassé)" className="w-full bg-white/[0.06] border border-white/10 rounded px-1.5 py-0.5 text-[10.5px] text-gray-100 outline-none" data-testid={`site-slot-quickadd-name-${slot.key}`} />
      <div className="flex items-center gap-1">
        <select value={subrole} onChange={(e) => setSubrole(e.target.value)} className="flex-1 min-w-0 bg-white/[0.06] border border-white/10 rounded px-1 py-0.5 text-[10px] text-gray-100" data-testid={`site-slot-quickadd-subrole-${slot.key}`}>
          {slot.subroles.map((s) => <option key={s} value={s}>{lib.subrole_labels?.[s] || s}</option>)}
        </select>
        <button type="button" onClick={submit} disabled={busy || !name.trim()} className="h-6 px-2 rounded text-[10px] font-semibold bg-amber-400 text-black disabled:opacity-40" data-testid={`site-slot-quickadd-submit-${slot.key}`}>
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Créer'}
        </button>
        <button type="button" onClick={onCancel} className="h-6 px-1.5 rounded text-[10px] text-gray-400 hover:text-white">Annuler</button>
      </div>
    </div>
  );
};

/** SiteFeatureMaterials — V15.37 : sélecteur « Référence » par emplacement (façade, toiture, sol…) d'une zone. */
export const SiteFeatureMaterials = ({ projectId, feature, lib, onChange, onLibraryChanged }) => {
  const [adding, setAdding] = useState(null);
  const slots = lib?.slots?.[feature.kind] || [];
  if (!slots.length) return null;
  const mats = lib?.materials || [];
  const set = (key, id) => onChange({ materials: { ...(feature.materials || {}), [key]: id || undefined } });
  return (
    <div className="mt-1.5 space-y-1.5" onClick={(e) => e.stopPropagation()} data-testid={`site-feature-materials-${feature.id}`}>
      <div className="text-[9.5px] uppercase tracking-[0.12em] text-gray-400 flex items-center gap-1"><Palette className="w-3 h-3" /> Références Matériauthèque</div>
      {slots.map((slot) => {
        const rec = mats.filter((m) => slot.subroles.includes(m.subrole));
        const others = mats.filter((m) => !slot.subroles.includes(m.subrole));
        const cur = feature.materials?.[slot.key] || '';
        return (
          <div key={slot.key} className="text-[10px]">
            <div className="flex items-center gap-1">
              <span className="text-gray-400 w-[86px] shrink-0 truncate">{slot.label}</span>
              <select value={cur} onChange={(e) => set(slot.key, e.target.value)} className="flex-1 min-w-0 bg-white/[0.06] border border-white/10 rounded px-1 py-0.5 text-gray-100" data-testid={`site-slot-select-${slot.key}`}>
                <option value="">— Aucune —</option>
                {rec.length > 0 && <optgroup label="Recommandés">{rec.map((m) => <option key={m.material_id} value={m.material_id}>{m.name}{m.finish ? ` · ${m.finish}` : ''}</option>)}</optgroup>}
                {others.length > 0 && <optgroup label="Autres matériaux du projet">{others.map((m) => <option key={m.material_id} value={m.material_id}>{m.name} ({m.role_label})</option>)}</optgroup>}
              </select>
              <button type="button" title="Nouveau matériau" onClick={() => setAdding(adding === slot.key ? null : slot.key)}
                      className={`h-5 w-5 inline-flex items-center justify-center rounded border ${adding === slot.key ? 'border-amber-300 text-amber-200' : 'border-white/10 text-gray-300 hover:bg-white/10'}`} data-testid={`site-slot-add-${slot.key}`}>
                <Plus className="w-3 h-3" />
              </button>
            </div>
            {adding === slot.key && (
              <QuickAddInline projectId={projectId} slot={slot} lib={lib} onCancel={() => setAdding(null)}
                              onCreated={async (id) => { setAdding(null); await onLibraryChanged?.(); set(slot.key, id); }} />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default SiteFeatureMaterials;
